import JSZip from "jszip";
import { saveAs } from 'file-saver';
import axios from "axios";
import { Asset } from "../state/types";
import { getBgrCarouselImages } from "../utils/assetImages";

/**
 * Options for file downloads
 */
interface FileDownloadConfig {
    /** Media types to exclude (exteriors, cropped, etc). By default, all media is downloaded */
    exclusions?: string[];
    /** Indexes of media types to include (2nd pano image, 7th exterior, etc) */
    indexInclusions?: IndexInclusion[];
}

/**
 * Object to specify which indexes of specific media types to include
 */
interface IndexInclusion {
    mediaType: string;
    indexes: number[];
}

/**
 * Individual media file object
 */
interface Media {
    imageUrlCf: string;
    mediaType: string;
    mediaIdx: number;
}

/**
 * Set of media of a specific type
 */
interface MediaSet {
    mediaType: string;
    media: Media[];
}

/**
 * Download a zip file of images for the current asset
 * 
 * @param asset - Asset object containing image information
 * @param config - Optional configuration for filtering which files to download
 * @returns Promise that resolves when the download is complete
 */
export async function downloadFilesAsync(asset: Asset, config?: FileDownloadConfig): Promise<void> {
    const zip = new JSZip();
    const mediaList = extractImages(asset);
    const filteredMedia = filterMedia(mediaList, config);

    try {
        await Promise.all(filteredMedia.map(async (media) => {
            if (media.imageUrlCf) {
                const pathname = new URL(media.imageUrlCf).pathname;   // e.g. /.../uc-01.mp4
                const match = pathname.match(/\.([a-zA-Z0-9]+)$/);
                const ext = match ? match[1] : "jpg"; // default to .bin if no match
                const fileContent = await retrieveFileAsync(media.imageUrlCf);
                const fileName = `${media.mediaType}/${media.mediaType}-${media.mediaIdx}.${ext}`;
                zip.file(fileName, fileContent);
            }
        }));

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zipBlob, `${asset.assetIdentifier}.zip`);
    } catch (error) {
        console.error("Failed to download files:", error);
        throw new Error("Failed to create download package");
    }
}

/**
 * Filter media list based on provided configuration
 * 
 * @param mediaList - Complete list of media
 * @param config - Filter configuration
 * @returns Filtered list of media
 */
function filterMedia(mediaList: Media[], config?: FileDownloadConfig): Media[] {
    if (!config) return mediaList;

    let result = [...mediaList];

    // Apply exclusions
    if (config.exclusions?.length) {
        result = result.filter(media => !config.exclusions?.includes(media.mediaType));
    }

    // Apply index inclusions
    if (config.indexInclusions?.length) {
        const inclusionMap = new Map(
            config.indexInclusions.map(inc => [inc.mediaType, inc.indexes])
        );

        result = result.filter(media => {
            const allowedIndexes = inclusionMap.get(media.mediaType);
            return !allowedIndexes || allowedIndexes.includes(media.mediaIdx);
        });
    }

    return result;
}

/**
 * Extract all images from the asset object
 * 
 * @param asset - Asset object containing media information
 * @returns List of media objects
 */
function extractImages(asset: Asset): Media[] {
    const bgrImages = getBgrCarouselImages(asset);

    const mediaSets: MediaSet[] = [
        { mediaType: 'exterior', media: asset.imageExterior ?? [] },
        { mediaType: 'manual', media: asset.imageManual ?? [] },
        { mediaType: 'pano', media: asset.imagePano ?? [] },
        { mediaType: 'featured', media: asset.imageFeatured ?? [] },
        { mediaType: 'mask', media: asset.imageMask ?? [] },
        { mediaType: 'crop', media: asset.imageCrop ?? [] },
        { mediaType: 'underCarriage', media: asset.imageUndercarriage ?? [] },
        { mediaType: 'bgr', media: bgrImages }
    ];

    return mediaSets.flatMap(mediaSet =>
        mediaSet.media.map((rawMedia: any, index) => ({
            mediaType: mediaSet.mediaType,
            imageUrlCf: rawMedia.imageUrlCf,
            mediaIdx: index + 1
        }))
    );
}

/**
 * Retrieve a file from a URL as a blob
 * 
 * @param url - URL of the file to download
 * @returns Promise that resolves with the file data
 */
async function retrieveFileAsync(url: string): Promise<Blob> {
    try {
        const response = await axios.get(url, { responseType: 'blob' });
        return response.data;
    } catch (error) {
        console.error(`Failed to retrieve file from ${url}:`, error);
        throw new Error('Error accessing image');
    }
}

/**
 * Download a single file (image, video, etc.) by URL
 *
 * @param fileUrl - The direct file URL
 * @param filename - Optional filename for saving (default: extracted from URL)
 */
export async function downloadSingleFile(fileUrl: string, filename?: string): Promise<void> {
  try {
    const fileContent = await retrieveFileAsync(fileUrl);

    // Try to extract from URL path
    let defaultName = fileUrl.split("/").pop()?.split("?")[0] || "download.jpg";

    // If the caller provided a filename, ensure extension matches the URL
    if (filename && !filename.includes(".")) {
      const ext = defaultName.includes(".") ? defaultName.split(".").pop() : "jpg";
      defaultName = `${filename}.${ext}`;
    }

    saveAs(fileContent, filename || defaultName);
  } catch (error) {
    console.error("Failed to download file:", error);
  }
}
